import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';

import { filterFieldType } from '@features/filter/static/filterUtil';
import { PROJECT_TYPES } from '@features/project-management-tools/constents';

import { storeItemInGlobalFilter } from '@store/actions/FilterAction';

import CommonImage from '@/components/CommonImage';
import InputField from '@/components/InputField';
import useDebounce from '@/hooks/useDebounce';
import Chip from '@/features/filter/Chip';
import { ReactComponent as KanbanIcon } from '@/assets/icons/kanban-view.svg';
import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar.svg';
import { ReactComponent as GridIcon } from '@/assets/icons/grid.svg';
import { ReactComponent as ListIcon } from '@/assets/icons/single-list.svg';

const MyProjectTopbar = memo(
	({
		projectCount = 0,
		onOpenCreateProjectModal,
		onFilterToggle,
		onSearch,
		currentDate,
		onPreviousMonth,
		onNextMonth,
		onToday,
		viewMode,
		onViewModeChange,
		setProjectView,
		projectView
	}) => {
		const [isShowSearch, setIsShowSearch] = useState(true);
		const [searchValue, setSearchValue] = useState('');
		const debouncedSearchValue = useDebounce(searchValue, 500);
		const searchRef = useRef(null);
		const dispatch = useDispatch();

		const handleSearch = useCallback((e) => {
			setSearchValue(e.target.value);
		}, []);

		const toggleSearch = useCallback(() => {
			setIsShowSearch((prev) => !prev);
		}, []);

		const handleOpenCreateModal = useCallback(() => {
			onOpenCreateProjectModal((prev) => !prev);
		}, [onOpenCreateProjectModal]);

		useEffect(() => {
			if (onSearch) {
				onSearch(debouncedSearchValue);
			}
		}, [debouncedSearchValue, onSearch]);

		useEffect(() => {
			if (isShowSearch && searchRef.current) {
				searchRef.current.focus();
			}
		}, [isShowSearch]);

		const projectLabel = projectCount > 1 ? 'Projects' : 'Project';

		const formatCurrentDate = () => {
			return currentDate
				.toLocaleDateString('en-US', {
					month: 'short',
					year: 'numeric'
				})
				.toUpperCase();
		};

		const onItemClick = (item) => {
			dispatch(
				storeItemInGlobalFilter({
					name: filterFieldType['PROJECT_TYPE']['idName'],
					key: item.id,
					value: item.value,
					identifier: `${filterFieldType['PROJECT_TYPE']['idName']}_${item.id}`,
					checked: true
				})
			);
		};

		return (
			<div className="top-header my-project-topbar bg-white">
				<div className="page-title-area d-flex align-items-center justify-content-between flex-wrap">
					<div className="top-header-left d-flex align-items-center justify-content-between">
						<div className="left-content d-flex align-items-center gap-2">
							<div
								onClick={onFilterToggle}
								className="filter-toggle-btn cursor-pointer d-flex align-items-center gap-2"
							>
								<CommonImage src="/icons/filter24.svg" />
								<p className="medium-14 gray_dark_02 weight-500 white-space-nowrap">
									{projectCount} {projectLabel}
								</p>
							</div>
							{projectView === 'CALENDAR' && (
								<>
									<div className="date-title">
										<p className="semibold-16 gray_dark_01 white-space-nowrap">
											{formatCurrentDate()}
										</p>
									</div>
									<div className="date-nav-buttons d-grid gap-1">
										<button
											className="icon-button left-btn border-0"
											onClick={onPreviousMonth}
										>
											<CommonImage src="/icons/Left-arrow.svg" width={24} height={24} />
										</button>
										<span
											className="current-date regular-12 gray_dark_01 px-3 py-1 d-flex align-items-center"
											onClick={onToday}
										>
											Today
										</span>
										<button
											className="icon-button right-btn border-0"
											onClick={onNextMonth}
										>
											<CommonImage
												src="/icons/right-arrow.svg"
												width={24}
												height={24}
											/>
										</button>
									</div>
								</>
							)}
						</div>
					</div>
					{projectView === 'CALENDAR' && (
						<div className="top-header-middle ml-2">
							<div className="inner-tabs calendar-tabs">
								<ul>
									{['Day', 'Week', 'Month', 'Year'].map((mode) => (
										<li
											className={`regular-14 gray_dark_02 weight-500 px-3 py-1 border-0 ${viewMode === mode ? 'active' : ''}`}
											key={mode}
											onClick={() => {
												if (mode === 'Day') {
													onToday();
												} else {
													onViewModeChange(mode);
												}
											}}
										>
											{mode}
										</li>
									))}
								</ul>
							</div>
						</div>
					)}

					<div className="top-header-right">
						<div className="filter-actions compare-filter-actions">
							{projectView !== 'CARD' && (
								<div className="form-group mb-0 pr-2">
									<CustomDropdown
										type="addItem"
										title="Project Type"
										items={PROJECT_TYPES?.map((item) => ({
											id: item.id,
											value: item.value
										}))}
										selectedItem={'Project Type'}
										onItemClick={onItemClick}
										className="border-0"
									/>
								</div>
							)}
							<div className="form-group size32 m-0 mr-2 search-filter filter-with-animation">
								<div className={`with-icon right-icon ${isShowSearch ? '' : 'hidden'}`}>
									<InputField
										type="text"
										placeholder="Project title"
										value={searchValue}
										name="search"
										ref={searchRef}
										onChange={handleSearch}
										withWrapper={false}
									/>
									<span className="right-icon" onClick={toggleSearch}>
										<CommonImage src="/icons/search.png" alt="search" />
									</span>
								</div>
							</div>
							<div className="design-view-sort d-flex align-items-center gap-2 mr-2">
								<div className="kanban-icon" onClick={() => setProjectView('CARD')}>
									<GridIcon
										width={24}
										height={24}
										alt="list"
										className={`${projectView === 'CARD' ? 'active' : ''} cursor-pointer`}
									/>
								</div>
								<div className="kanban-icon" onClick={() => setProjectView('LIST')}>
									<ListIcon
										width={24}
										height={24}
										alt="card"
										className={`${projectView === 'LIST' ? 'active' : ''} cursor-pointer`}
									/>
								</div>
								<div className="kanban-icon">
									<KanbanIcon
										className={`${projectView === 'KANBAN' ? 'active' : ''} cursor-pointer`}
										onClick={() => setProjectView('KANBAN')}
										width={24}
										height={24}
									/>
								</div>
								<div className="calendar-icon">
									<CalendarIcon
										src="/icons/calendar.svg"
										alt="add"
										className={`${projectView === 'CALENDAR' ? 'active' : ''} cursor-pointer`}
										width={24}
										height={24}
										onClick={() => setProjectView('CALENDAR')}
									/>
								</div>
							</div>
							<div className="add-new-task">
								<button className="button text size32 px-0" onClick={handleOpenCreateModal}>
									<CommonImage src="/icons/add-round.svg" alt="add" />
									<span className="ml-1 gray_dark_01">Project</span>
								</button>
							</div>
						</div>
					</div>
				</div>
				<div className="chip-container">
					<div className="chips pt-2 pb-2 pl-3">
						<Chip maxChar={80} />
					</div>
				</div>
			</div>
		);
	}
);

export default MyProjectTopbar;
